import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Minus, Plus, Loader2, Utensils, Flame, Waves, Leaf, X, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatISK, formatDate, formatApiErrorDetail } from "@/lib/api";
import CancellationPolicy from "@/components/CancellationPolicy";
import PhoneInput from "@/components/PhoneInput";

// Placeholder menu content — replace `items` per course when final menus arrive.
// Petit Fours are served with all three menus.
const PETIT_FOURS = {
  course: "Petit Fours",
  note: "Served with all three menus",
  items: [
    { name: "Birch", desc: "White chocolate ganache • Birch syrup • Freeze-dried Icelandic blueberries" },
    { name: "Midnight Sun", desc: "Licorice ganache • Burnt caramel • A touch of Icelandic Brennivín" },
  ],
};

const JOURNEYS = [
  {
    key: "fire",
    label: "Fire",
    icon: Flame,
    title: "The Highlands & Volcanoes",
    descriptors: ["Bold", "Smoky", "Elemental"],
    accent: "#D2552E",
    image: "https://images.unsplash.com/photo-1619266465172-02a857c3556d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    intro: "Bold, smoky flavours inspired by Iceland's volcanic landscapes — volcanoes, smoke, charcoal and bold Nordic flavours.",
    courses: [
      { course: "Canapés", items: [
        { name: "Reindeer tartare", desc: "Wild blueberries • juniper" },
        { name: "Smoked Arctic char", desc: "Horseradish • dill • Icelandic rye" },
        { name: "Lamb croquette", desc: "Fermented garlic • herbs" },
      ] },
      { course: "Starter", items: [
        { name: "Slow-cooked Icelandic cod", desc: "Brown butter • cauliflower • trout roe • Champagne sauce" },
      ] },
      { course: "Main Course", items: [
        { name: "Roasted Icelandic lamb chops", desc: "Smoked celeriac purée • charred kale • toasted hazelnuts • Vin Jaune jus" },
      ] },
      { course: "Dessert", items: [
        { name: "Rhubarb", desc: "Licorice • Italian meringue", tableside: "Caramelized tableside over glowing charcoal." },
      ] },
      PETIT_FOURS,
    ],
  },
  {
    key: "ocean",
    label: "Ocean",
    icon: Waves,
    title: "The North Atlantic",
    descriptors: ["Pristine", "Delicate", "Briny"],
    accent: "#3E7CA6",
    image: "https://images.unsplash.com/photo-1500043357865-c6b8827edf10?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    intro: "A celebration of Iceland's pristine waters — the North Atlantic, ice, sea mist and pristine seafood.",
    courses: [
      { course: "Canapés", items: [
        { name: "Hand-dived Westfjords scallop", desc: "Green apple • sorrel" },
        { name: "Smoked Arctic char", desc: "Horseradish • dill" },
        { name: "Langoustine tartare", desc: "Sea herbs • citrus" },
      ] },
      { course: "Starter", items: [
        { name: "Cured halibut", desc: "Buttermilk • cucumber • dill • trout roe" },
      ] },
      { course: "Main Course", items: [
        { name: "Pan-roasted monkfish", desc: "Champagne beurre blanc • sea asparagus • leeks • brown butter potatoes" },
      ] },
      { course: "Dessert", items: [
        { name: "Skyr", desc: "White chocolate • sea buckthorn • birch syrup" },
      ] },
      PETIT_FOURS,
    ],
  },
  {
    key: "earth",
    label: "Earth",
    icon: Leaf,
    title: "Foraged Iceland",
    descriptors: ["Plant-based", "Wild", "Foraged"],
    accent: "#5E8B4C",
    image: "https://images.pexels.com/photos/33931095/pexels-photo-33931095.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200",
    intro: "A fully plant-based menu celebrating Iceland's wild nature — wild herbs, forests, moss and foraged ingredients.",
    courses: [
      { course: "Canapés", items: [
        { name: "Smoked beetroot tartare", desc: "" },
        { name: "Grilled king oyster mushroom", desc: "" },
        { name: "Potato crisp", desc: "Seaweed • herb emulsion" },
      ] },
      { course: "Starter", items: [
        { name: "Roasted cauliflower", desc: "Plant-based Champagne sauce • toasted hazelnuts • sea herbs" },
      ] },
      { course: "Main Course", items: [
        { name: "Roasted celeriac", desc: "Wild mushrooms • charred kale • fermented onions • juniper jus" },
      ] },
      { course: "Dessert", items: [
        { name: "Rhubarb", desc: "Licorice • Italian aquafaba meringue", tableside: "Finished tableside over live charcoal." },
      ] },
      PETIT_FOURS,
    ],
  },
];

export default function Dining() {
  const [config, setConfig] = useState(null);
  const [openJourney, setOpenJourney] = useState(null);
  const [availability, setAvailability] = useState({});
  const [date, setDate] = useState("");
  const [seating, setSeating] = useState("");
  const [party, setParty] = useState(1);
  const [journeys, setJourneys] = useState({ Fire: 0, Ocean: 0, Earth: 0 });
  const [beverageCount, setBeverageCount] = useState(0);
  const [customer, setCustomer] = useState({ full_name: "", email: "", phone: "", allergies: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [stock, setStock] = useState(null);

  const loadAvailability = () => {
    api.get("/dinner/availability").then((res) => setAvailability(res.data.availability)).catch(() => {});
  };
  const loadStock = () => api.get("/dinner/stock").then((res) => setStock(res.data)).catch(() => {});

  useEffect(() => {
    api.get("/config").then((res) => setConfig(res.data)).catch(() => {});
    loadAvailability();
    loadStock();
  }, []);

  if (!config) {
    return <div className="min-h-[60vh] flex items-center justify-center text-gray-500 text-xs uppercase tracking-[0.3em]">Loading…</div>;
  }

  const price = config.dinner_price;
  const beveragePrice = config.beverage_price || 0;
  const beverages = Math.min(beverageCount, party);
  const total = party * price + beverages * beveragePrice;
  const feePercent = config.card_fee_percent || 0;
  const cardFee = Math.round(total * feePercent / 100);
  const grandTotal = total + cardFee;
  const remaining = date && seating && availability[date] ? availability[date][seating].remaining : null;
  const journeyAssigned = journeys.Fire + journeys.Ocean + journeys.Earth;
  const journeyLeft = party - journeyAssigned;
  const stockLeft = (label) => (stock && stock[label] ? stock[label].remaining : Infinity);
  const pairingLeft = stock && stock.pairing ? stock.pairing.remaining : Infinity;

  const setJourney = (key, delta) => {
    setJourneys((prev) => {
      const next = Math.max(0, prev[key] + delta);
      const others = prev.Fire + prev.Ocean + prev.Earth - prev[key];
      if (delta > 0 && others + next > party) return prev; // cannot exceed party size
      if (delta > 0 && next > stockLeft(key)) { toast.error(`Only ${stockLeft(key)} ${key} portion(s) left`); return prev; }
      return { ...prev, [key]: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return toast.error("Choose a date");
    if (!seating) return toast.error("Choose a seating");
    if (journeyAssigned !== party) return toast.error(`Choose a journey for all ${party} guest${party > 1 ? "s" : ""}`);
    if (!customer.full_name || !customer.email || !customer.phone) return toast.error("Please complete your details");
    setSubmitting(true);
    try {
      const res = await api.post("/bookings/dinner", {
        date, seating, party_size: party,
        journey_fire: journeys.Fire, journey_ocean: journeys.Ocean, journey_earth: journeys.Earth,
        beverage_count: beverages,
        customer,
      });
      const checkout = await api.post("/payments/checkout", {
        booking_id: res.data.booking.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reservation failed");
      loadAvailability();
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="dining-page">
      <div className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img src="https://static.prod-images.emergentagent.com/jobs/44292365-21c1-4f3d-88e3-fd6ad71c5275/images/4d6a5f856630047b56b5a7a31670b77e4a20d2891ce7201597a0a0a5bc14e894.png" alt="Fine dining inside a black stretch tent" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-300 mb-5">The Dining Experience · {formatISK(price)} per person</p>
          <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92] max-w-3xl">Three immersive menus inspired by Iceland</h1>
          <p className="text-gray-300 mt-5 max-w-xl leading-relaxed">Rather than traditional menu categories, choose one of three culinary journeys inspired by Iceland's landscapes and natural elements. Fire, smoke, ice and aroma transform each course into an experience of its own.</p>
        </div>
      </div>

      {/* Choose Your Journey */}
      <section data-testid="journeys-section" className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-20">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.35em] text-gray-500 mb-4">The Menu</p>
          <h2 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl lg:text-5xl">Choose Your Journey</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {JOURNEYS.map((j, i) => {
            const Icon = j.icon;
            return (
              <motion.button
                key={j.key}
                type="button"
                data-testid={`journey-card-${j.key}`}
                onClick={() => setOpenJourney(j)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative overflow-hidden border border-white/10 text-left h-[440px] flex flex-col justify-end transition-all duration-500 hover:border-white/40"
              >
                <img src={j.image} alt={j.title} className="absolute inset-0 w-full h-full object-cover opacity-55 transition-transform duration-[1200ms] group-hover:scale-110 group-hover:opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-[#050505]/20" />
                <div className="absolute top-0 left-0 right-0 h-1 transition-all duration-500" style={{ backgroundColor: j.accent, opacity: 0.85 }} />
                <div className="relative z-10 p-8">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full border mb-6 transition-colors duration-500" style={{ borderColor: `${j.accent}66` }}>
                    <Icon size={22} style={{ color: j.accent }} />
                  </span>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-3xl leading-none">{j.label}</h3>
                  <p className="text-gray-300 text-sm mt-2">{j.title}</p>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mt-4">{j.descriptors.join(" · ")}</p>
                  <span className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-[0.2em] font-bold text-white group-hover:gap-3 transition-all">
                    Explore {j.label} <ArrowRight size={15} />
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>

      <form onSubmit={submit} className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">01 — Select Date</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {config.dates.map((d) => (
                <button key={d} type="button" data-testid={`dinner-date-${d}`} onClick={() => { setDate(d); setSeating(""); }}
                  className={`py-4 px-3 border text-center transition-all ${date === d ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                  <span className="block text-xs uppercase tracking-[0.1em]">{formatDate(d)}</span>
                </button>
              ))}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-5">02 — Choose Sitting</p>
            <div className="grid grid-cols-2 gap-3">
              {(date && availability[date] ? Object.keys(availability[date]) : config.seatings).map((s) => {
                const info = date && availability[date] ? availability[date][s] : null;
                const blocked = info?.blocked;
                const rem = info ? info.remaining : config.seating_capacity;
                const soldOut = date && !blocked && rem <= 0;
                const disabled = !!(blocked || soldOut);
                return (
                  <button key={s} type="button" disabled={disabled} data-testid={`seating-${s}`} onClick={() => setSeating(s)}
                    className={`py-6 px-4 border text-center transition-all disabled:opacity-40 disabled:cursor-not-allowed ${seating === s ? "bg-white text-black border-white" : "border-white/15 text-gray-300 hover:border-white/50"}`}>
                    <span className="block font-display font-bold text-2xl uppercase tracking-tight">{s}</span>
                    <span className="block text-[10px] uppercase tracking-[0.15em] mt-2 opacity-70">
                      {!date ? "Select date first" : blocked ? "This evening is not available" : soldOut ? "Sold out" : "Available"}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">03 — Party Size</p>
            <div className="flex items-center justify-between py-4 border-b border-white/10">
              <p className="text-sm uppercase tracking-[0.1em]">Guests</p>
              <div className="flex items-center gap-5">
                <button type="button" data-testid="party-minus" onClick={() => setParty(Math.max(1, party - 1))} disabled={party <= 1} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30"><Minus size={15} /></button>
                <span data-testid="party-value" className="w-6 text-center font-display font-bold text-lg">{party}</span>
                <button type="button" data-testid="party-plus" onClick={() => setParty(party + 1)} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors"><Plus size={15} /></button>
              </div>
            </div>
            {remaining !== null && party > remaining && (
              <p data-testid="capacity-warning" className="text-[#C44536] text-xs uppercase tracking-[0.1em] mt-3">This seating can't fit your whole party — please choose another seating or date</p>
            )}
          </section>

          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">04 — Choose Your Journey</p>
            <p className="text-xs text-gray-500 mb-4">Assign a culinary journey to each of your {party} guest{party > 1 ? "s" : ""} so our kitchen can prepare
              {journeyLeft > 0 ? <span className="text-[#C44536]"> · {journeyLeft} still to assign</span> : journeyLeft < 0 ? <span className="text-[#C44536]"> · {Math.abs(journeyLeft)} too many, please reduce</span> : <span className="text-gray-400"> · all assigned</span>}
            </p>
            <div className="space-y-1">
              {JOURNEYS.map((j) => {
                const Icon = j.icon;
                const left = stockLeft(j.label);
                const soldOut = left <= 0;
                const plusDisabled = journeyAssigned >= party || journeys[j.label] >= left;
                return (
                  <div key={j.key} className={`flex items-center justify-between py-4 border-b border-white/10 ${soldOut ? "opacity-50" : ""}`}>
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border" style={{ borderColor: `${j.accent}66` }}>
                        <Icon size={16} style={{ color: j.accent }} />
                      </span>
                      <div>
                        <p className="text-sm uppercase tracking-[0.1em]">{j.label}</p>
                        <p className="text-[11px] text-gray-500">{j.title}</p>
                        {stock && (soldOut
                          ? <p data-testid={`journey-${j.key}-stock`} className="text-[11px] uppercase tracking-[0.1em] text-[#C44536] mt-0.5">Sold out</p>
                          : left <= 10 ? <p data-testid={`journey-${j.key}-stock`} className="text-[11px] text-amber-400 mt-0.5">Only {left} left</p> : null)}
                      </div>
                    </div>
                    <div className="flex items-center gap-5">
                      <button type="button" data-testid={`journey-${j.key}-minus`} onClick={() => setJourney(j.label, -1)} disabled={journeys[j.label] <= 0} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30"><Minus size={15} /></button>
                      <span data-testid={`journey-${j.key}-value`} className="w-6 text-center font-display font-bold text-lg">{journeys[j.label]}</span>
                      <button type="button" data-testid={`journey-${j.key}-plus`} onClick={() => setJourney(j.label, 1)} disabled={plusDisabled} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30"><Plus size={15} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">05 — Beverage Pairing <span className="text-gray-600 normal-case tracking-normal">(optional)</span></p>
            <p className="text-xs text-gray-500 mb-4">A curated beverage pairing for each course — {formatISK(beveragePrice)} per guest.</p>
            <div className="flex items-center justify-between py-4 border-b border-white/10">
              <div>
                <p className="text-sm uppercase tracking-[0.1em]">Beverage Pairing</p>
                <p className="text-[11px] text-gray-500">{formatISK(beveragePrice)} per guest · {beverages} of {party} selected</p>
                {stock && (pairingLeft <= 0
                  ? <p data-testid="pairing-stock" className="text-[11px] uppercase tracking-[0.1em] text-[#C44536] mt-0.5">Sold out</p>
                  : pairingLeft <= 10 ? <p data-testid="pairing-stock" className="text-[11px] text-amber-400 mt-0.5">Only {pairingLeft} left</p> : null)}
              </div>
              <div className="flex items-center gap-5">
                <button type="button" data-testid="beverage-minus" onClick={() => setBeverageCount((c) => Math.max(0, Math.min(c, party) - 1))} disabled={beverages <= 0} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30"><Minus size={15} /></button>
                <span data-testid="beverage-value" className="w-6 text-center font-display font-bold text-lg">{beverages}</span>
                <button type="button" data-testid="beverage-plus" onClick={() => { if (beverages >= pairingLeft) { toast.error(`Only ${pairingLeft} pairing(s) left`); return; } setBeverageCount((c) => Math.min(party, c + 1)); }} disabled={beverages >= party || beverages >= pairingLeft} className="w-9 h-9 border border-white/20 hover:border-white/60 flex items-center justify-center transition-colors disabled:opacity-30"><Plus size={15} /></button>
              </div>
            </div>
            <button type="button" data-testid="beverage-whole-table" onClick={() => setBeverageCount(beverages >= party ? 0 : Math.min(party, pairingLeft))} className="mt-4 text-[11px] uppercase tracking-[0.15em] text-gray-400 hover:text-white transition-colors underline underline-offset-4">
              {beverages >= party ? "Remove pairing for the table" : "Add pairing for the whole table"}
            </button>
          </section>

          <section>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">06 — Your Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Full name</label>
                <input data-testid="dinner-customer-name" required value={customer.full_name} onChange={(e) => setCustomer({ ...customer, full_name: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Email</label>
                <input type="email" data-testid="dinner-customer-email" required value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Phone</label>
                <PhoneInput testid="dinner-customer-phone" required onChange={(v) => setCustomer((prev) => ({ ...prev, phone: v }))} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Notes (optional)</label>
                <input data-testid="dinner-customer-notes" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} className="bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-white transition-colors w-full" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Allergies / dietary requirements (optional)</label>
                <textarea data-testid="dinner-customer-allergies" rows={3} value={customer.allergies} onChange={(e) => setCustomer({ ...customer, allergies: e.target.value })} placeholder="Please list any allergies or dietary requirements so our kitchen can prepare" className="bg-transparent border border-white/20 rounded-none px-3 py-3 text-white focus:outline-none focus:border-white transition-colors w-full resize-none placeholder:text-gray-600" />
              </div>
            </div>
          </section>
        </div>

        <div className="lg:col-span-1">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:sticky lg:top-28 bg-[#0C0C0E] border border-white/10 p-8">
            <Utensils className="text-white/70 mb-5" size={20} />
            <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">Summary</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-gray-400"><span>Date</span><span className="text-white">{date ? formatDate(date) : "—"}</span></div>
              <div className="flex justify-between text-gray-400"><span>Sitting</span><span className="text-white">{seating || "—"}</span></div>
              <div className="flex justify-between text-gray-400"><span>Journey</span><span className="text-white text-right">{journeyAssigned > 0 ? Object.entries(journeys).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(" · ") : "—"}</span></div>
              <div className="flex justify-between text-gray-400"><span>{formatISK(price)} × {party}</span><span className="text-white">{formatISK(price * party)}</span></div>
              {beverages > 0 && (
                <div className="flex justify-between text-gray-400"><span data-testid="summary-beverage-label">Pairing {formatISK(beveragePrice)} × {beverages}</span><span data-testid="summary-beverage-amount" className="text-white">{formatISK(beveragePrice * beverages)}</span></div>
              )}
            </div>
            <div className="flex justify-between items-end mt-6 pt-6 border-t border-white/10">
              <span className="text-xs uppercase tracking-[0.15em] text-gray-400">Total</span>
              <span data-testid="dinner-total" className="font-display font-extrabold text-3xl">{formatISK(grandTotal)}</span>
            </div>
            {cardFee > 0 && (
              <div className="flex justify-between text-[11px] text-gray-500 mt-3">
                <span data-testid="dinner-fee-label">Incl. {feePercent}% card fee</span>
                <span data-testid="dinner-fee-amount">{formatISK(cardFee)}</span>
              </div>
            )}
            <button type="submit" data-testid="confirm-dinner-booking" disabled={submitting} className="w-full mt-8 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Redirecting to payment</> : "Pay & Confirm"}
            </button>
            <p className="text-[10px] uppercase tracking-[0.15em] text-gray-600 mt-4 text-center">Secure card payment</p>
          </motion.div>
          <CancellationPolicy className="mt-6" />
        </div>
      </form>

      {/* Journey menu panel — slide-in on desktop, full-screen on mobile */}
      <AnimatePresence>
        {openJourney && (
          <motion.div
            data-testid="journey-panel"
            className="fixed inset-0 z-[60] flex justify-end"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpenJourney(null)} />
            <motion.aside
              className="relative h-full w-full sm:max-w-md bg-[#0A0A0C] border-l border-white/10 overflow-y-auto"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", ease: [0.22, 1, 0.36, 1], duration: 0.45 }}
            >
              <div className="relative h-56">
                <img src={openJourney.image} alt={openJourney.title} className="absolute inset-0 w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
                <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: openJourney.accent }} />
                <button
                  type="button"
                  data-testid="journey-panel-close"
                  onClick={() => setOpenJourney(null)}
                  className="absolute top-5 right-5 w-10 h-10 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                >
                  <X size={18} />
                </button>
                <div className="absolute bottom-5 left-6 right-6">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border mb-3" style={{ borderColor: `${openJourney.accent}66` }}>
                    {(() => { const I = openJourney.icon; return <I size={20} style={{ color: openJourney.accent }} />; })()}
                  </span>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-4xl leading-none">{openJourney.label}</h3>
                  <p className="text-gray-300 text-sm mt-1">{openJourney.title}</p>
                </div>
              </div>

              <div className="p-6 md:p-8">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 mb-5">{openJourney.descriptors.join(" · ")}</p>
                <p className="text-gray-300 text-sm leading-relaxed mb-8">{openJourney.intro}</p>

                <div className="space-y-7">
                  {openJourney.courses.map((c) => (
                    <div key={c.course} data-testid={`course-${c.course.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[11px] uppercase tracking-[0.25em] whitespace-nowrap" style={{ color: openJourney.accent }}>{c.course}</span>
                        <span className="flex-1 h-px bg-white/10" />
                        {c.note && <span className="text-[10px] uppercase tracking-[0.15em] text-gray-600 whitespace-nowrap">{c.note}</span>}
                      </div>
                      <div className="space-y-3">
                        {c.items.map((it, idx) => (
                          <div key={idx}>
                            <p className="text-sm text-white leading-snug">{it.name}</p>
                            {it.desc && <p className="text-xs text-gray-400 leading-relaxed mt-0.5">{it.desc}</p>}
                            {it.tableside && <p className="text-xs italic text-gray-500 mt-1" style={{ color: `${openJourney.accent}` }}>{it.tableside}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  data-testid="journey-panel-reserve"
                  onClick={() => { setOpenJourney(null); document.querySelector('[data-testid="dinner-date-' + (config.dates[0] || "") + '"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                  className="w-full mt-10 bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-xs font-bold py-4 transition-colors inline-flex items-center justify-center gap-3"
                >
                  <ArrowLeft size={15} /> Reserve Your Table
                </button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
